import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Grid,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

import Button from '../components/Button'
import Select from '../components/Select'
import GridHeader from '../components/GridHeader'
import SelectionBar from '../components/selection/SelectionBar'
import TableCellTeam from '../components/table/TableCellTeam'
import SortableTableHead from '../components/SortableTableHead'
import DialogSpostamento from '../components/DialogSpostamento'
import DialogSpostamentoInfo from '../components/DialogSpostamentoInfo'
import GridCircularProgress from '../components/GridCircularProgress'
import { config } from '../settings/configs'
import {
  colours,
  titlesIscrizioni as titles,
  phrasesIscrizioni as phrases,
  TIPOLOGIE_SPOSTAMENTO,
} from '../settings/settings'
import { selectSpostamentiSorted } from '../store/selectors'
import { useFeedback } from '../hooks/feedback-hook'
import { fromYYYYMMDDtoDDMMYYYY, mailTo } from '../hooks/utils-functions'
import {
  useGetAffiliazioniLightQuery,
  useGetImpostazioniContattiQuery,
  useGetIscrizioniQuery,
  useGetSpostamentiQuery,
  useLazyGetSpostamentoByIdQuery,
  useCreateSpostamentoMutation,
  useUpdateSpostamentoReplyMutation,
} from '../hooks/api-slice'
import { setSelectedStatus, setSelectedGroup } from '../container/spostamenti'

// status selection
const STATUSES = ['Tutti', 'In sospeso', 'Accettati', 'Rifiutati']

// group selection (for non-admin)
const GROUPS = ['Tutti', 'Ricevuti', 'Inviati']

const Spostamenti = () => {
  const { admin, codiceUtente } = useSelector(state => state.home.infoUtente)
  const { data: contatti } = useGetImpostazioniContattiQuery()
  const { data: affiliazioniLight } = useGetAffiliazioniLightQuery(undefined, {
    skip: !admin,
  })
  const selectedCampionatoCalendariPubblicati = useSelector(
    state => state.selection.selectedCampionatoCalendariPubblicati,
  )
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )
  const availableCategorie = useSelector(
    state => state.selection.availableCategorie,
  )
  const selectedCategoria = useSelector(
    state => state.selection.selectedCategoria,
  )
  const availableGironi = useSelector(state => state.selection.availableGironi)
  const selectedGirone = useSelector(state => state.selection.selectedGirone)
  const selectedTeam = useSelector(state => state.selection.selectedTeam)
  const selectedNumeroGara = useSelector(
    state => state.selection.selectedNumeroGara,
  )
  const selectedStatus = useSelector(state => state.spostamenti.selectedStatus)
  const selectedGroup = useSelector(state => state.spostamenti.selectedGroup)

  const sArgs = { selectedCampionato, codiceUtente, admin }
  const { isFetching: isFetchingS } = useGetSpostamentiQuery(sArgs, {
    skip: !(
      selectedCampionato &&
      (admin || (codiceUtente && selectedCampionatoCalendariPubblicati))
    ),
  })
  const { isFetching: isFetchingI } = useGetIscrizioniQuery(
    { campionato: selectedCampionato, iFilter: 'nomi' },
    { skip: !selectedCampionato },
  )
  const spostamenti = useSelector(state =>
    selectSpostamentiSorted(state, sArgs),
  )

  const [mode, setMode] = useState()
  const [spostamento, setSpostamento] = useState()

  const [triggerGetSpostamentoById] = useLazyGetSpostamentoByIdQuery()
  const [createSpostamentoMutation] = useCreateSpostamentoMutation()
  const [updateSpostamentoReplyMutation] = useUpdateSpostamentoReplyMutation()

  const spostamentiN = spostamenti?.filter(
    s =>
      (s.categoria === selectedCategoria ||
        selectedCategoria === availableCategorie[0] ||
        !selectedCategoria) &&
      (s.girone === selectedGirone ||
        selectedGirone === availableGironi[0] ||
        !selectedGirone ||
        !admin) &&
      (s.squadraCasaID === selectedTeam ||
        s.squadraTrasfertaID === selectedTeam ||
        !selectedTeam ||
        !admin) &&
      (s.numeroGara === selectedNumeroGara || !selectedNumeroGara),
  )

  const filterSpostamentiAdmin = x =>
    x?.filter(s => {
      if (selectedStatus === STATUSES[0]) return true

      if (selectedStatus === STATUSES[1])
        return ![true, false].includes(s.acceptedBySocietaRisposta)

      if (selectedStatus === STATUSES[2])
        return s.acceptedBySocietaRisposta === true

      if (selectedStatus === STATUSES[3])
        return s.acceptedBySocietaRisposta === false

      return false
    })

  const filterSpostamentiNonAdmin = x =>
    x?.filter(s => {
      if (selectedGroup === GROUPS[1])
        return codiceUtente === s.codiceSocietaRisposta

      if (selectedGroup === GROUPS[2])
        return codiceUtente === s.codiceSocietaDomanda

      return true
    })

  const dispatch = useDispatch()

  document.title = `${config?.name} - Spostamenti`

  let columnsSpostamenti = [
    { label: 'Stato', sortable: false },
    { label: 'Richiesto il', sortable: true, sortingField: 'timeDomanda' },
    { label: 'Gara', sortable: true, sortingField: 'numeroGara' },
    { label: 'Categoria', sortable: true, sortingField: 'categoria' },
    { label: 'Squadra in casa', sortable: false },
    { label: 'Squadra in trasferta', sortable: false },
    { label: 'Tipologia', sortable: false },
  ]
  if (admin)
    columnsSpostamenti = [
      ...columnsSpostamenti,
      { label: 'Richiesto da', sortable: false },
    ]

  useEffect(() => {
    if (!selectedStatus) dispatch(setSelectedStatus(STATUSES[0]))
  }, [dispatch, selectedStatus])

  useEffect(() => {
    if (!selectedGroup) dispatch(setSelectedGroup(GROUPS[0]))
  }, [dispatch, selectedGroup])

  const [openSpostamentoInfo, setOpenSpostamentoInfo] = useState()
  const [openSpostamentoAdmin, setOpenSpostamentoAdmin] = useState()

  const { setFeedback } = useFeedback()

  const handleClickOpenSpostamentoInfo = () => setOpenSpostamentoInfo(true)
  const handleClickOpenSpostamentoAdmin = () => setOpenSpostamentoAdmin(true)

  const handleCloseSpostamentoInfo = () => {
    setOpenSpostamentoInfo()
    setSpostamento()
    setMode()
  }

  const handleCloseSpostamentoAdmin = () => setOpenSpostamentoAdmin()

  const openSpostamento = async (id, mode) => {
    const result = await triggerGetSpostamentoById(id)
    if (!result?.error) {
      setMode(mode)
      setSpostamento(result?.data)
      handleClickOpenSpostamentoInfo()
    }
  }

  const replySpostamento = async spostamento => {
    if (!spostamento?.checkAccept && !spostamento?.checkReject) {
      setFeedback('Lo spostamento va accettato o rifiutato.')
      return
    }

    const reply = spostamento?.checkAccept ? 'accetta' : 'rifiuta'
    const arg = { id: spostamento?.id, reply }
    const result = await updateSpostamentoReplyMutation(arg)
    if (!result?.error) {
      handleCloseSpostamentoInfo()
      setFeedback(
        spostamento?.checkAccept
          ? phrases.phraseSpostamentoAccetta
          : phrases.phraseSpostamentoRifiuta,
        false,
        spostamento?.checkAccept
          ? titles.titleSpostamentoAccetta
          : titles.titleSpostamentoRifiuta,
      )
    }
  }

  const performSpostamentoAdmin = async spostamento => {
    const body = {
      codiceUtente: String(spostamento?.codiceSocietaDomanda),
      idGara: spostamento?.selectedGara,
      tipologia: spostamento?.selectedFunction,
      nuovaData: spostamento?.newData,
      nuovaOra: spostamento?.newOra,
    }
    const result = await createSpostamentoMutation(body)

    if (!result?.error) {
      handleCloseSpostamentoAdmin()
      setFeedback(
        phrases.phraseSpostamentoDomandaSuccess,
        false,
        titles.titleSpostamentoDomanda,
      )
    }
  }

  return (
    <Grid container paddingX={2} spacing={2} sx={{ width: '100%' }}>
      <GridHeader h2 text='Spostamenti' />
      <Grid container alignItems='center' sx={{ width: '100%' }}>
        <SelectionBar
          data={spostamenti}
          yCampionato
          yCategoria
          yGirone={admin}
          yTeam={admin}
          yNumeroGara={admin}
        />
        {admin && (
          <Grid size={{ xs: 12, md: 3 }}>
            <Select
              label='Stato'
              value={selectedStatus}
              options={STATUSES}
              onChange={i => dispatch(setSelectedStatus(i))}
            />
          </Grid>
        )}
        {!admin && (
          <Grid size={{ xs: 12, md: 2 }}>
            <Select
              label='Filtro'
              value={selectedGroup}
              options={GROUPS}
              onChange={i => dispatch(setSelectedGroup(i))}
            />
          </Grid>
        )}
        {admin && (
          <Grid size={{ xs: 12, md: 'grow' }}>
            <center>
              <Button onClick={handleClickOpenSpostamentoAdmin}>
                Nuovo spostamento
              </Button>
            </center>
          </Grid>
        )}
      </Grid>
      {((isFetchingS || isFetchingI) && <GridCircularProgress />) ||
        (!spostamentiN && (
          <GridHeader text='Elenco degli spostamenti non trovato.' />
        )) ||
        (spostamentiN.length === 0 && (
          <GridHeader text='Nessuno spostamento trovato.' />
        )) || (
          <Grid container sx={{ width: '100%' }}>
            {!admin && (
              <Grid size={12}>
                <p>
                  Per domande sugli spostamenti scrivere a{' '}
                  {mailTo(contatti?.MailSpostamenti)}.
                </p>
              </Grid>
            )}
            {(!admin &&
              filterSpostamentiNonAdmin(spostamentiN)?.length === 0 && (
                <Grid size={12}>
                  <center>
                    <b>Nessuno spostamento.</b>
                  </center>
                </Grid>
              )) || (
              <TableContainer component={Paper}>
                <Table size='small'>
                  <SortableTableHead
                    table={spostamentiN}
                    columns={columnsSpostamenti}
                    tableName='spostamenti'
                  />
                  <TableBody>
                    {(admin
                      ? filterSpostamentiAdmin(spostamentiN)
                      : filterSpostamentiNonAdmin(spostamentiN)
                    )?.map((s, index) => {
                      const isRisposta =
                        s.codiceSocietaRisposta === codiceUtente
                      const isAccepted = s.acceptedBySocietaRisposta === true
                      const isRejected = s.acceptedBySocietaRisposta === false
                      const isPending = ![true, false].includes(
                        s.acceptedBySocietaRisposta,
                      )
                      const isReplyable = isRisposta && isPending

                      return (
                        <TableRow
                          key={index}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: isReplyable
                              ? colours.highlight
                              : colours.white,
                            ':hover': {
                              backgroundColor: colours.greyVeryLight,
                            },
                          }}
                          onClick={() =>
                            openSpostamento(
                              s.id,
                              isReplyable ? 'reply' : 'view',
                            )
                          }
                        >
                          <TableCell>
                            <Grid>
                              <Tooltip
                                placement='right'
                                title={
                                  (isAccepted && 'Accettato') ||
                                  (isRejected && 'Rifiutato') ||
                                  (isPending && 'In sospeso') ||
                                  'Sconosciuto'
                                }
                              >
                                {(isAccepted && (
                                  <CheckIcon color='success' />
                                )) ||
                                  (isRejected && <ClearIcon color='error' />) ||
                                  (isPending && (
                                    <AccessTimeIcon color='warning' />
                                  )) || <>?</>}
                              </Tooltip>
                            </Grid>
                          </TableCell>
                          <TableCell>
                            {fromYYYYMMDDtoDDMMYYYY(s.timeDomanda)}
                          </TableCell>
                          <TableCell>{s.numeroGara}</TableCell>
                          <TableCell>{s.categoria}</TableCell>
                          <TableCellTeam id={s.squadraCasaID} />
                          <TableCellTeam id={s.squadraTrasfertaID} />
                          <TableCell>
                            {TIPOLOGIE_SPOSTAMENTO[s.tipologia]?.short}
                          </TableCell>
                          {admin && (
                            <TableCell>
                              {affiliazioniLight?.find(
                                a =>
                                  a.codiceSocieta ===
                                  Number(s.codiceSocietaDomanda),
                              )?.nomeSocieta || s.codiceSocietaDomanda}
                            </TableCell>
                          )}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        )}
      <DialogSpostamentoInfo
        open={openSpostamentoInfo}
        handleClose={handleCloseSpostamentoInfo}
        triggerFunction={
          mode === 'reply' ? replySpostamento : handleCloseSpostamentoInfo
        }
        spostamentoInit={spostamento}
        mode={mode}
      />
      <DialogSpostamento
        open={openSpostamentoAdmin}
        handleClose={handleCloseSpostamentoAdmin}
        triggerFunction={performSpostamentoAdmin}
      />
    </Grid>
  )
}

export default Spostamenti
